import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  fetchInterviews,
  interviewsErrorSelector,
  interviewsFetchedSelector,
  interviewsLoadingSelector,
  interviewsSelector
} from '../../redux/features/interviews/interviewsSlice';
import { currentCompanySelector } from '../../redux/features/company/companiesSlice';
import {
  trackInterviewsPageView,
  trackInterviewEventClicked
} from '../../helpers/analytics';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    minHeight: 'calc(100vh - 64px)'
  },
  card: {
    marginBottom: theme.spacing(3)
  },
  calendarContainer: {
    padding: theme.spacing(2),
    '& .fc': {
      fontFamily: theme.typography.fontFamily
    },
    '& .fc-button': {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      textTransform: 'capitalize',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      }
    },
    '& .fc-button-active': {
      backgroundColor: theme.palette.primary.dark
    },
    '& .fc-event': {
      cursor: 'pointer'
    },
    '& .fc-daygrid-event': {
      padding: '2px 4px',
      borderRadius: '4px'
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  },
  detailRow: {
    marginBottom: theme.spacing(1.5)
  },
  detailLabel: {
    fontWeight: 600,
    marginRight: theme.spacing(1)
  },
  statusChip: {
    marginTop: theme.spacing(1)
  }
}));

// Event colors based on status
const getEventColor = (status) => {
  switch (status) {
    case 'scheduled':
      return '#1976d2'; // Blue
    case 'completed':
      return '#2e7d32'; // Green
    case 'cancelled':
      return '#d32f2f'; // Red
    default:
      return '#757575'; // Gray
  }
};

const InterviewsView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const company = useSelector(currentCompanySelector);

  const interviews = useSelector(interviewsSelector);
  const loading = useSelector(interviewsLoadingSelector);
  const fetched = useSelector(interviewsFetchedSelector);
  const error = useSelector(interviewsErrorSelector);

  const [selectedInterview, setSelectedInterview] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Fetch interviews when component mounts
  useEffect(() => {
    if (company?.id && !fetched && !loading) {
      console.log(
        '📅 [InterviewsView] Fetching interviews for company:',
        company.id
      );
      dispatch(fetchInterviews({ companyId: company.id }));
    }
  }, [dispatch, company, fetched, loading]);

  // Track page view
  useEffect(() => {
    if (window.gtag && fetched) {
      trackInterviewsPageView(interviews.length);
    }
  }, [interviews.length, fetched]);

  // Convert interviews to FullCalendar events
  const calendarEvents = useMemo(() => {
    return interviews.map((interview) => ({
      id: interview.id,
      title: `${interview.candidateName || 'Candidate'} - ${
        interview.jobTitle || 'Interview'
      }`,
      start: moment(interview.interviewDate).toISOString(),
      end: moment(interview.interviewDate)
        .add(interview.durationMinutes, 'minutes')
        .toISOString(),
      backgroundColor: getEventColor(interview.status),
      borderColor: getEventColor(interview.status),
      extendedProps: {
        ...interview
      }
    }));
  }, [interviews]);

  const handleEventClick = (clickInfo) => {
    const interview = clickInfo.event.extendedProps;
    setSelectedInterview(interview);
    setDetailsDialogOpen(true);

    // Track analytics
    if (window.gtag) {
      trackInterviewEventClicked(
        interview.id,
        interview.candidateName,
        interview.jobTitle
      );
    }
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedInterview(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading && !fetched) {
    return (
      <Box className={classes.root}>
        <Card>
          <CardContent>
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Card>
          <CardContent>
            <Box className={classes.emptyState}>
              <Typography variant="h5" color="error" gutterBottom>
                Error Loading Interviews
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {error}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Box mb={2}>
            <Typography variant="h4" gutterBottom>
              Interview Calendar
            </Typography>
            <Typography variant="body2" color="textSecondary">
              View and manage all scheduled interviews ({interviews.length}{' '}
              total)
            </Typography>
          </Box>

          {interviews.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h5" gutterBottom>
                No Interviews Scheduled
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Schedule interviews with candidates from the Applications page.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.calendarContainer}>
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={calendarEvents}
                eventClick={handleEventClick}
                height="auto"
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
                slotLabelFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
                allDaySlot={false}
                nowIndicator={true}
                weekends={true}
                editable={false}
                selectable={false}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Interview Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Interview Details</DialogTitle>
        <DialogContent>
          {selectedInterview && (
            <Box>
              <Box className={classes.detailRow}>
                <Typography variant="body1">
                  <span className={classes.detailLabel}>Candidate:</span>
                  {selectedInterview.candidateName}
                </Typography>
              </Box>

              <Box className={classes.detailRow}>
                <Typography variant="body1">
                  <span className={classes.detailLabel}>Job:</span>
                  {selectedInterview.jobTitle}
                </Typography>
              </Box>

              <Box className={classes.detailRow}>
                <Typography variant="body1">
                  <span className={classes.detailLabel}>Date & Time:</span>
                  {moment(selectedInterview.interviewDate).format(
                    'MMMM D, YYYY [at] h:mm A'
                  )}
                </Typography>
              </Box>

              <Box className={classes.detailRow}>
                <Typography variant="body1">
                  <span className={classes.detailLabel}>Duration:</span>
                  {selectedInterview.durationMinutes} minutes
                </Typography>
              </Box>

              {selectedInterview.timezone && (
                <Box className={classes.detailRow}>
                  <Typography variant="body1">
                    <span className={classes.detailLabel}>Timezone:</span>
                    {selectedInterview.timezone}
                  </Typography>
                </Box>
              )}

              {selectedInterview.interviewerName && (
                <Box className={classes.detailRow}>
                  <Typography variant="body1">
                    <span className={classes.detailLabel}>Interviewer:</span>
                    {selectedInterview.interviewerName}
                  </Typography>
                </Box>
              )}

              {selectedInterview.locationUrl && (
                <Box className={classes.detailRow}>
                  <Typography variant="body1">
                    <span className={classes.detailLabel}>Location:</span>
                    <a
                      href={selectedInterview.locationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-all' }}>
                      {selectedInterview.locationUrl}
                    </a>
                  </Typography>
                </Box>
              )}

              {selectedInterview.description && (
                <Box className={classes.detailRow}>
                  <Typography variant="body1">
                    <span className={classes.detailLabel}>Notes:</span>
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                    {selectedInterview.description}
                  </Typography>
                </Box>
              )}

              <Box className={classes.detailRow}>
                <Chip
                  label={selectedInterview.status.toUpperCase()}
                  color={getStatusColor(selectedInterview.status)}
                  className={classes.statusChip}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterviewsView;
